import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, ArrowDownIcon } from 'lucide-react';
import { HeroContactPanel } from './HeroContactPanel';
const HERO_IMAGE = 'https://cdn.magicpatterns.com/uploads/2na9DzBHbeg2WsGFNjh9h3/rp-hero-cnc-aluminum.png';
const HERO_ALT = 'Illustrative close-up of a CNC lathe turning an aluminum part with a controlled chip';
function ChipFlowField() {
  return <svg aria-hidden="true" focusable="false" viewBox="0 0 600 400" preserveAspectRatio="none" className="pointer-events-none absolute inset-0 h-full w-full" data-id="element-115" data-mp="apl8oyqqbxeq">
      <g stroke="#C7CDD3" strokeOpacity="0.1" strokeWidth="1" data-id="element-116" data-mp="pkolal1dd9h">
        <line x1="0" y1="88" x2="600" y2="88" data-id="element-117" data-mp="3rworpue28fcm" />
        <line x1="0" y1="312" x2="600" y2="312" data-id="element-118" data-mp="3rxysircas1my" />
        <line x1="96" y1="0" x2="96" y2="400" data-id="element-119" data-mp="1v4eteu7q0lqn" />
      </g>
      <g stroke="#B66A33" strokeOpacity="0.28" strokeWidth="1" fill="none" data-id="element-120" data-mp="1v3vb3ce5rvc3">
        <path d="M0 340 C 140 340, 210 268, 268 214" data-id="element-121" data-mp="1q525e65458mh" />
        <path d="M0 358 C 152 358, 232 282, 300 226" data-id="element-122" data-mp="1q3hf4spu0buf" />
      </g>
    </svg>;
}
function ApplicationViewLabel({
  className = ''
}: {
  className?: string;
}) {
  return <p className={`inline-flex items-center gap-2 border border-white/25 bg-navy/80 px-3 py-1.5 font-mono text-[9.5px] leading-none tracking-label text-aluminum backdrop-blur-sm ${className}`} data-id="element-123" data-mp="1rx0dns2znk99">
      <span aria-hidden="true" className="h-px w-4 bg-copper" data-id="element-124" data-mp="1hxriwx0au1lu" />
      PRECISION TURNING / APPLICATION VIEW
    </p>;
}
export function Hero() {
  return <section aria-labelledby="hero-heading" className="relative w-full overflow-hidden bg-navy" data-id="element-125" data-mp="2amgded6un8om">
      {/* Desktop: right-anchored application photograph bleeding to the edge */}
      <div className="absolute inset-y-0 right-0 hidden w-[64%] lg:block xl:w-[62%]" data-id="element-126" data-mp="20vlpl4dyg112">
        <img src={HERO_IMAGE} alt={HERO_ALT} width={1376} height={768} loading="eager" decoding="async" className="h-full w-full object-cover object-[58%_center]" data-id="element-127" data-mp="2oly8tbenhk6y" />
        <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-r from-navy via-navy/85 to-navy/15" data-id="element-128" data-mp="3373rnljc3kb1" />
        <div aria-hidden="true" className="absolute inset-y-0 left-0 w-40 bg-gradient-to-r from-navy to-transparent" data-id="element-129" data-mp="2y7h86ucwx7ts" />
        <ApplicationViewLabel className="absolute bottom-8 right-10" data-id="element-130" data-mp="2p7kjwtufqaz9" />
      </div>

      <ChipFlowField data-id="element-131" data-mp="2k9sqtwi20ith" />

      <div className="relative mx-auto grid max-w-shell px-5 pb-14 pt-12 lg:min-h-[600px] lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] lg:items-center lg:px-10 lg:py-24" data-id="element-132" data-mp="2kaauvfayk1by">
        <div className="lg:mx-auto lg:w-[calc(100%+12px)] lg:max-w-[652px] lg:-translate-x-[6px] lg:pr-0" data-id="element-133" data-mp="2aj7vchxofues">
          <p className="flex items-start gap-3 font-mono text-[10px] leading-[1.7] tracking-label text-copper" data-id="element-134" data-mp="26ixg0enzvrn9">
            <span aria-hidden="true" className="mt-[8px] h-px w-8 shrink-0 bg-copper" data-id="element-135" data-mp="21hq5i7ugwecw" />
            <span className="min-w-0" data-id="element-136" data-mp="1wkq80lboojxe">
              SHEAR GEOMETRY CUTTING TOOLS • PRECISION-MACHINING EXPERIENCE
              SINCE 1984
            </span>
          </p>
          <h1 id="hero-heading" className="mt-6 max-w-[19ch] font-display text-[clamp(2.15rem,4.4vw,3.9rem)] font-medium leading-[1.03] tracking-[-0.02em] text-white" data-id="element-137" data-mp="gzpdw79o13j0">
            High-rake CNC lathe tooling for aluminum, copper &amp; titanium
          </h1>
          <p className="rp-measure mt-7 text-[16px] leading-[1.7] text-aluminum" data-id="element-138" data-mp="3phvaetq72fah">
            Shear Geometry brings together external holders, APGX inserts,
            starter kits and replacement components developed for demanding CNC
            turning applications. Contact Robertson Precision to confirm current
            availability and application fit.
          </p>

          <div className="mt-9 flex flex-col gap-3 sm:flex-row sm:items-center" data-id="element-139" data-mp="36qufcgs733w7">
            <Link to="/#product-guidance" data-analytics="product-guidance-cta" data-analytics-location="hero" className="inline-flex min-h-[52px] items-center justify-center gap-2 bg-cta px-7 text-[14.5px] font-semibold text-navy transition-colors duration-150 ease-precise hover:bg-white" data-id="element-140" data-mp="3aro236twicni">
              Request Product Guidance
              <ArrowRightIcon aria-hidden="true" className="h-4 w-4" data-id="element-141" data-mp="2wmnrthn8g619" />
            </Link>
            <Link to="/#products" className="inline-flex min-h-[52px] items-center justify-center gap-2 border border-aluminum/40 px-7 text-[14.5px] font-medium text-white transition-colors duration-150 ease-precise hover:border-copper hover:text-cta" data-id="element-142" data-mp="3adsjnxkczu11">
              Explore the Tooling System
              <ArrowDownIcon aria-hidden="true" className="h-4 w-4" data-id="element-143" data-mp="3fclufi89h6xi" />
            </Link>
          </div>

          <p className="mt-6 font-mono text-[11px] leading-relaxed tracking-[0.05em] text-aluminum/80" data-id="element-144" data-mp="1pyyw7ubzgrlf">
            For CNC lathes only. Not intended for manual lathes.
          </p>

          {/* Tablet / mobile: stacked below the CTAs and CNC-lathe note */}
          <HeroContactPanel idSuffix="stacked" className="mt-10 lg:hidden" data-id="element-145" data-mp="5fwsrn2ccq8r" />
        </div>

        {/* Desktop: compact contact module in the hero’s right grid area */}
        <div className="hidden lg:flex lg:justify-center" data-id="element-146" data-mp="3mw4p4lqvaolx">
          <HeroContactPanel idSuffix="desktop" className="lg:w-[340px] lg:max-w-[340px]" data-id="element-147" data-mp="3rupnsgqh1089" />
        </div>
      </div>

      {/* Mobile / tablet: copy first, then a cropped 16:9 application view */}
      <div className="relative border-t border-white/10 lg:hidden" data-id="element-148" data-mp="3hyuy51nty4vz">
        <div className="relative aspect-[16/9] w-full overflow-hidden" data-id="element-149" data-mp="33ybsg16u7awz">
          <img src={HERO_IMAGE} alt={HERO_ALT} width={1376} height={768} loading="eager" decoding="async" className="h-full w-full object-cover object-[62%_center]" data-id="element-150" data-mp="38wdd2qjwviak" />
          <ApplicationViewLabel className="absolute bottom-4 left-5" data-id="element-151" data-mp="174h2bj5kicgu" />
        </div>
      </div>
    </section>;
}